import { IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import { isNotNullOrUndefined } from '@vy/utils';
import { useState } from 'react';
import { VyTableColumnSettings } from '../../../../components/new-design/table/table-column-settings/VyTableColumnSettings';
import {
  type FilterField,
  type FilterLineType,
} from '../../../../components/new-design/vyTableFilter';
import type { ColumnConfig } from '../../../../hooks/use-grid-column-settings';
import { reportIconButtonSx, reportToggleGroupSx, TOOLBAR_CONTROL_HEIGHT } from '../../reports-components/report-toolbar-styles';
import { RevenueReportToolbar } from '../../reports-components/RevenueReportToolbar';
import { MANDATORY_COLUMNS_COUNT, type MonthlyValueMode } from './RecurringRevenueReportTable/RecurringRevenueReportTableColumns';

type RecurringRevenueReportToolbarProps = {
  onSearchChange: (value: string) => void;
  searchValue?: string;
  fromDate: Date | null;
  onFromDateChange: (date: Date | null) => void;
  toDate: Date | null;
  onToDateChange: (date: Date | null) => void;
  filterFields: FilterField[];
  filters: FilterLineType[];
  onFiltersChange: (filters: FilterLineType[]) => void;
  onColumnSettingsOpen: () => void;
  onColumnSettingsClose: () => void;
  columnSettings: ColumnConfig[];
  onColumnVisibilityChange: (field: string, isVisible: boolean) => void;
  onExport: () => void;
  showChart: boolean;
  onShowChartChange: (show: boolean) => void;
  viewMode: MonthlyValueMode;
  onViewModeChange: (mode: MonthlyValueMode) => void;
};

export const RecurringRevenueReportToolbar: React.FC<RecurringRevenueReportToolbarProps> = ({
  onSearchChange,
  searchValue,
  fromDate,
  onFromDateChange,
  toDate,
  onToDateChange,
  filterFields,
  filters,
  onFiltersChange,
  onColumnSettingsOpen,
  onColumnSettingsClose,
  columnSettings,
  onColumnVisibilityChange,
  onExport,
  showChart,
  onShowChartChange,
  viewMode,
  onViewModeChange,
}) => {
  const [columnSettingsAnchorEl, setColumnSettingsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isColumnSettingsOpen = isNotNullOrUndefined(columnSettingsAnchorEl);

  const trailingActions = (
    <>
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={(_event, next) => {
          if (next) {
            onViewModeChange(next as MonthlyValueMode);
          }
        }}
        size="small"
        sx={reportToggleGroupSx(TOOLBAR_CONTROL_HEIGHT)}
        data-vy="recurring-revenue-report-arr-mrr-toggle"
      >
        <ToggleButton value="arr">ARR</ToggleButton>
        <ToggleButton value="mrr">MRR</ToggleButton>
      </ToggleButtonGroup>

      <IconButton
        onClick={(event) => {
          setColumnSettingsAnchorEl(event.currentTarget);
          onColumnSettingsOpen();
        }}
        data-vy="recurring-revenue-report-column-settings"
        sx={reportIconButtonSx}
      >
        <VyIcon icon="Grid" style={{ transform: 'scale(0.8)' }} />
      </IconButton>

      <VyTableColumnSettings
        anchorEl={columnSettingsAnchorEl}
        open={isColumnSettingsOpen}
        onClose={() => {
          setColumnSettingsAnchorEl(null);
          onColumnSettingsClose();
        }}
        columns={columnSettings}
        handleColumnVisibilityChange={onColumnVisibilityChange}
        mandatoryColumnsCount={MANDATORY_COLUMNS_COUNT}
      />
    </>
  );

  return (
    <RevenueReportToolbar
      onSearchChange={onSearchChange}
      searchValue={searchValue}
      fromDate={fromDate}
      onFromDateChange={onFromDateChange}
      toDate={toDate}
      onToDateChange={onToDateChange}
      useDateRangePicker
      filterFields={filterFields}
      filters={filters}
      onFiltersChange={onFiltersChange}
      showChart={showChart}
      onShowChartChange={onShowChartChange}
      onExport={onExport}
      trailingActions={trailingActions}
    />
  );
};
